import { useCallback } from "react";
import {
  useGetSocialLinksQuery,
  useUpdateSocialLinkMutation,
  useDeleteSocialLinkMutation,
  GetSocialLinksDocument,
} from "@ugitgud/legos/queries/auth";
import { useSetHeaderBgMutation } from "@ugitgud/legos/queries/lol";
import { useRecurlySubscriptionStatus } from "@ugitgud/legos/hooks/usePremiumHooks";
import { GET_SUMMONER_PROFILE_SIMPLE } from "../summoner-profiles/profile-init";
import { INTERNAL_CURRENT_SEASON } from "@ugg/shared/utils/season-helpers";

export type SocialPlatform = "twitch" | "youtube" | "x";

export interface SocialLink {
  socialLinkUuid: string;
  platform: string;
  username: string;
  url: string;
  creationTime?: number | null;
  updatedTime?: number | null;
}

interface UseSocialLinksOptions {
  onUpdateCompleted?: () => void;
  onUpdateError?: (error: Error) => void;
  onDeleteCompleted?: () => void;
  onDeleteError?: (error: Error) => void;
}

export function useSocialLinks(options: UseSocialLinksOptions = {}) {
  const { isActive: hasRecurlySubscription, loading: subscriptionLoading } = useRecurlySubscriptionStatus();

  const {
    data: socialLinksData,
    loading: linksLoading,
    refetch: refetchLinks,
  } = useGetSocialLinksQuery({
    context: { clientName: "auth-api" },
  });

  const [updateSocialLinkMutation, { loading: updating, error: updateError }] = useUpdateSocialLinkMutation({
    context: { clientName: "auth-api" },
    refetchQueries: [{ query: GetSocialLinksDocument, context: { clientName: "auth-api" } }],
    awaitRefetchQueries: true,
    onCompleted: options.onUpdateCompleted,
    onError: options.onUpdateError,
  });

  const [deleteSocialLinkMutation, { loading: deleting, error: deleteError }] = useDeleteSocialLinkMutation({
    context: { clientName: "auth-api" },
    refetchQueries: [{ query: GetSocialLinksDocument, context: { clientName: "auth-api" } }],
    awaitRefetchQueries: true,
    onCompleted: options.onDeleteCompleted,
    onError: options.onDeleteError,
  });

  const updateSocialLink = useCallback(
    (platform: SocialPlatform, username: string) => {
      return updateSocialLinkMutation({
        variables: {
          platform,
          username,
        },
      });
    },
    [updateSocialLinkMutation],
  );

  const deleteSocialLink = useCallback(
    (platform: SocialPlatform) => {
      return deleteSocialLinkMutation({
        variables: {
          platform,
        },
      });
    },
    [deleteSocialLinkMutation],
  );

  const socialLinks = socialLinksData?.getSocialLinks ?? [];

  const getLinkByPlatform = useCallback(
    (platform: SocialPlatform): SocialLink | null => {
      const link = socialLinks.find((link) => link?.platform === platform);
      return link ?? null;
    },
    [socialLinks],
  );

  const currentValues = {
    twitchName: getLinkByPlatform("twitch")?.username || "",
    youtubeName: getLinkByPlatform("youtube")?.username || "",
    twitterName: getLinkByPlatform("x")?.username || "",
  };

  return {
    socialLinks,
    currentValues,
    updateSocialLink,
    deleteSocialLink,
    refetchLinks,
    loading: linksLoading || subscriptionLoading,
    updating,
    deleting,
    saving: updating || deleting,
    updateError,
    deleteError,
    hasRecurlySubscription,
  };
}

interface UseSetHeaderBgOptions {
  regionId: string;
  riotUserName: string;
  riotTagLine: string;
  onCompleted?: () => void;
  onError?: (error: Error) => void;
}

export function useSetHeaderBg(options: UseSetHeaderBgOptions) {
  const { regionId, riotUserName, riotTagLine } = options;
  const variables = {
    regionId: regionId?.toLowerCase(),
    riotUserName: riotUserName?.toLowerCase(),
    riotTagLine: riotTagLine?.toLowerCase(),
    seasonId: INTERNAL_CURRENT_SEASON,
  };

  const [setHeaderBgMutation, { data, loading, error }] = useSetHeaderBgMutation({
    context: { clientName: "lol" },
    refetchQueries: [
      {
        query: GET_SUMMONER_PROFILE_SIMPLE,
        variables,
        fetchPolicy: "network-only",
      },
    ],
    awaitRefetchQueries: true,
    onCompleted: options.onCompleted,
    onError: options.onError,
  });

  const setHeaderBg = useCallback(
    (headerBg: string) => {
      return setHeaderBgMutation({
        variables: { headerBg },
      });
    },
    [setHeaderBgMutation],
  );

  return {
    setHeaderBg,
    data,
    loading,
    error,
    success: data?.setHeaderBg?.success ?? false,
  };
}
